// The words Company intelligence prints (module 10 U52 to U59; B517). Pure, so each is tested. U32: no
// decision numbers, model names or build notes on a screen; times in the reader's own clock.
import type { LineView, ReadoutView } from './api.js';
import { dayMonth, monthShort } from '../day.js';

const DAY_MS = 86_400_000;

function dayOf(iso: string): { weekday: number; text: string } {
  const d = new Date(iso);
  return {
    weekday: d.getDay(),
    text: d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }),
  };
}

/**
 * What the read-out covers, in words: "Covers Monday 21 September", "Covers Friday 18 September and the weekend".
 * "Covers" because the tab above it is the day it was read on (BUGS.md I11): under the Fri 2 tab a bare "Thursday 1
 * October" read as a wrong date (Mike by pop-up, 8 Oct about 08:23 CEST, "All as proposed").
 */
export function coveredLabel(v: Pick<ReadoutView, 'coversFrom' | 'day'>): string | null {
  if (!v.coversFrom) return null;
  const from = dayOf(v.coversFrom);
  const readOn = v.day ? new Date(`${v.day}T12:00:00`).getDay() : null;
  return `Covers ${from.weekday === 5 && readOn === 1 ? `${from.text} and the weekend` : from.text}`;
}

/**
 * The read-out's span, for the page: what the morning covered and, when hours were added, the day they were
 * added on. "Covers Friday 18 September and the weekend, and today so far" on the day itself; "Covers Thursday
 * 17 September, and Friday" when Friday's read-out is read later.
 */
export function coveredWords(v: Pick<ReadoutView, 'coversFrom' | 'day' | 'addedAt'>, now: Date = new Date()): string | null {
  const covered = coveredLabel(v);
  if (!covered || !v.day || v.addedAt.length === 0) return covered;
  const readOn = new Date(`${v.day}T12:00:00`);
  if (readOn.toDateString() === now.toDateString()) return `${covered}, and today so far`;
  return `${covered}, and ${readOn.toLocaleDateString('en-GB', { weekday: 'long' })}`;
}

/** The hour the morning read-out was written, "07:00", or null before one is. */
export function morningHour(v: Pick<ReadoutView, 'writtenAt'>): string | null {
  return v.writtenAt ? new Date(v.writtenAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }) : null;
}

/** "11:02" today, "Mon 16:40" this week, "14 Sep" before that. */
export function whenOf(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  if (d.toDateString() === now.toDateString()) return hm;
  if (now.getTime() - d.getTime() < 6 * DAY_MS) return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${hm}`;
  return dayMonth(d);
}

/** "New 11:00": the hour an addition was written to. */
export function newTag(line: Pick<LineView, 'isNew' | 'addedAt'>): string | null {
  if (!line.isNew) return null;
  return `New ${new Date(line.addedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}`;
}

/** The source chips: "Email 2", "Chat", "Call". */
export function sourceChips(s: LineView['sources']): string[] {
  const one = (label: string, n: number) => (n > 1 ? `${label} ${n}` : label);
  const out: string[] = [];
  if (s.mail > 0) out.push(one('Email', s.mail));
  if (s.chat > 0) out.push(one('Chat', s.chat));
  if (s.call > 0) out.push(one('Call', s.call));
  return out;
}

/** "Written 07:00" and "added to at 10:00 and 11:00". */
export function writtenWords(v: Pick<ReadoutView, 'writtenAt' | 'addedAt'>): string[] {
  const hm = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const out: string[] = [];
  if (v.writtenAt) out.push(`Written ${hm(v.writtenAt)}`);
  if (v.addedAt.length > 0) {
    const hours = v.addedAt.map(hm);
    out.push(`Added to at ${hours.length === 1 ? hours[0] : `${hours.slice(0, -1).join(', ')} and ${hours.at(-1)}`}`);
  }
  return out;
}

/**
 * A day's tab: "Today" for today's read-out, "Fri 18" for another day this week, "Tue 25 Aug" for one further back
 * (the "Older" list runs thirty days, across a month's end). `day` is YYYY-MM-DD.
 */
export function dayTab(day: string, now: Date = new Date()): string {
  const d = new Date(`${day}T12:00:00`);
  if (d.toDateString() === now.toDateString()) return 'Today';
  const short = `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${d.getDate()}`;
  return now.getTime() - d.getTime() < 6 * DAY_MS ? short : `${short} ${monthShort(d)}`;
}

/** The noun after a count: "1 company", "4 companies". */
export function countWord(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** "6 more companies", "and 1 hand-on". */
export function moreWords(companiesLeft: number, handOns: number): string | null {
  const parts: string[] = [];
  if (companiesLeft > 0) parts.push(`${companiesLeft} more ${companiesLeft === 1 ? 'company' : 'companies'}`);
  if (handOns > 0) parts.push(`${handOns} hand-on${handOns === 1 ? '' : 's'}`);
  if (parts.length === 0) return null;
  return parts.join(' and ');
}

// ---- The two weeks and the search (B1694) -----------------------------------------------------------

const ymdLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Today in the reader's own calendar, YYYY-MM-DD. */
export function todayOf(now: Date = new Date()): string {
  return ymdLocal(now);
}

/**
 * The days on the page, newest first: every working day of the last fourteen, today included, and any other day of
 * those fourteen that holds a read-out (one written on a Saturday because it was asked for). A Saturday or a
 * Sunday with nothing is not a row: its news is in Monday's.
 */
export function recentDays(history: readonly { day: string }[], now: Date = new Date()): string[] {
  const held = new Set(history.map((h) => h.day));
  const out: string[] = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i, 12);
    const day = ymdLocal(d);
    if ((d.getDay() >= 1 && d.getDay() <= 5) || held.has(day)) out.push(day);
  }
  return out;
}

/** A day's row: "Today", "Yesterday", then "Monday 21 September". */
export function dayHeading(day: string, now: Date = new Date()): string {
  const d = new Date(`${day}T12:00:00`);
  if (day === ymdLocal(now)) return 'Today';
  if (day === ymdLocal(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 12))) return 'Yesterday';
  const long = d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  return d.getFullYear() === now.getFullYear() ? long : `${long} ${d.getFullYear()}`;
}

/**
 * What a closed day holds, in one line, so it can be passed over without opening: "4 companies moved, 1 hand-on
 * followed, 6 conversations read". A day with no read-out says so plainly, and today before the first one says when
 * it comes; an empty read says whether anything was read at all (B530: nothing read is not a quiet day).
 */
export function daySummaryWords(s: { companies: number | null; handOns: number | null; conversations: number } | undefined, day: string, now: Date = new Date()): string {
  if (!s) return day === ymdLocal(now) ? 'Nothing written yet today' : 'No read-out for this day';
  if (s.conversations === 0) return 'Nothing reached Cobrain to read';
  // A day older than the last fortnight carries no counts (B2325); the page draws none, and would say only what it read.
  if (s.companies === null || s.handOns === null) return `${s.conversations} ${countWord(s.conversations, 'conversation', 'conversations')} read`;
  const parts: string[] = [];
  parts.push(s.companies > 0 ? `${s.companies} ${countWord(s.companies, 'company', 'companies')} moved` : 'Nothing moved with a client, partner or deal');
  if (s.handOns > 0) parts.push(`${s.handOns} ${countWord(s.handOns, 'hand-on', 'hand-ons')} followed`);
  parts.push(`${s.conversations} ${countWord(s.conversations, 'conversation', 'conversations')} read`);
  return parts.join(', ');
}

/** Why a day picked in the search has no read-out: a weekend's news is in Monday's; before the first there is none. */
export function noReadoutWords(day: string, oldest: string | null): string {
  const d = new Date(`${day}T12:00:00`);
  // Written out, not asked of Intl with a year: en-GB puts a comma after the weekday when the year is in it.
  const name = `${d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} ${d.getFullYear()}`;
  if (d.getDay() === 0 || d.getDay() === 6) return `${name} is a weekend day: its news is in the Monday read-out.`;
  if (oldest && day < oldest) return `No read-out goes back to ${name}. The first is ${dayHeading(oldest)}.`;
  return `There is no read-out for ${name}.`;
}
